// Match-shape vocabulary — the ONE place the app words "what shape is this match".
//
// The engine-derived shapes arrive as a ready-made cell (the `match_group_label` column
// the server builds in reporting/match_groups.py), so nothing here recomputes a shape
// from the frames: no second definition of "which rows are one match", and no chance of
// the browser and the workbook disagreeing.
//
// matchShapeLabel is the exception, and it exists for the manual-match panels alone:
// a group the user has assembled but NOT yet applied has no engine label yet, so its
// badge has to be built client-side. It is a direct port of
// reporting/match_groups.py::_label, so an unapplied group reads exactly as it will
// once applied.

export const MATCH_GROUP_COL = "match_group_label";

// A plain cross-book pair. Must equal reporting/match_groups.py::PLAIN_PAIR_LABEL —
// isAggregate keys on it to tell "nothing special here" from a real group.
export const PLAIN_PAIR_LABEL = "1:1";

/** Does this Match Group cell describe anything other than an ordinary 1:1 pair? */
export function isAggregate(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  const s = String(value).trim();
  return s !== "" && s !== PLAIN_PAIR_LABEL;
}

/**
 * The Match Group label for a match of `nCri` CRI legs and `nIns` insurer legs.
 * Port of reporting/match_groups.py::_label — keep the two in step.
 *
 * `kind` names the pass that built the match (the parenthetical); omit it for a match
 * that carries no group id, which is exactly the plain 1:1 case.
 */
export function matchShapeLabel(nCri: number, nIns: number, kind?: string): string {
  const suffix = kind ? ` (${kind})` : "";
  if (nCri && nIns) {
    if (nCri === 1 && nIns === 1) return `${PLAIN_PAIR_LABEL}${suffix}`;
    return `${nCri}:${nIns}${suffix}`;
  }
  // One-sided: a within-ledger offset, whose legs both sit in the same book. A "1:0"
  // ratio would read as a missing leg rather than as what it is.
  const n = nCri || nIns;
  if (n > 1) return `${n} rows, same book${suffix}`;
  return kind || PLAIN_PAIR_LABEL;
}

/**
 * Extra cell class for a grid rendering the Match Group column: a pill, emphasised when
 * the row is part of something other than a plain pair. Shared by every grid so the
 * column looks the same in the statement tabs, the drill-downs and Side by Side.
 *
 * `colKey` may be a composite paired-frame key ("cri::match_group_label").
 */
export function matchGroupCellClass(colKey: string, value: unknown): string {
  if (!colKey.endsWith(MATCH_GROUP_COL)) return "";
  return isAggregate(value) ? "mgrp-cell is-agg" : "mgrp-cell";
}
